import { useCallback } from 'react';

import { useRealtimeContext } from './RealtimeContext';
import type { EventMap } from './types';

interface IUseRealtimePublishReturn<TEventMap extends EventMap> {
  /**
   * Publish an event to all subscribers.
   *
   * @param eventType - The event type to publish
   * @param payload - The event payload
   */
  publish: <K extends keyof TEventMap>(eventType: K, payload: TEventMap[K]) => void;
}

/**
 * Hook for publishing realtime events.
 *
 * Used primarily by the debugger panel to simulate events during development.
 *
 * @typeParam TEventMap - Map of event types to their payload types
 * @returns Object with publish function
 *
 * @example
 * ```tsx
 * import { useRealtimePublish } from '@feathr/hooks';
 *
 * function DebugPanel() {
 *   const { publish } = useRealtimePublish<FundraisingEventMap>();
 *
 *   const handleSummon = () => {
 *     publish('fundraising.donation.created', {
 *       id: 'mock-123',
 *       donor_first_name: 'John',
 *       donor_last_name: 'Doe',
 *       amount: 50,
 *       // ...other fields
 *     });
 *   };
 *
 *   return <Button onClick={handleSummon}>Summon Donation</Button>;
 * }
 * ```
 */
function useRealtimePublish<
  TEventMap extends EventMap = EventMap,
>(): IUseRealtimePublishReturn<TEventMap> {
  const { eventBus } = useRealtimeContext<TEventMap>();

  const publish = useCallback(
    <K extends keyof TEventMap>(eventType: K, payload: TEventMap[K]): void => {
      eventBus.publish(eventType, payload);
    },
    [eventBus],
  );

  return { publish };
}

export default useRealtimePublish;
export type { IUseRealtimePublishReturn };
